import React from "react";
import { format } from "date-fns";
import DataController from "@controllers/DataController";
import { Icon_Add_Evidence, Icon_ParaPass, Icon_Plus, Icon_ParaPD, Icon_CPG } from "@assets/images";
import { IonButton, IonImg } from "@ionic/react";
import { TimelineData } from "@interfaces/TimelineTypes";
import * as TimelineUtils from "@utils/timelineUtils";
import { IParapassBooksProgress, IParapassQuizData, IParapassUsageData, IPlusUsageData } from "@interfaces/Interfaces";
import { isUK, PARAPASS_APP_NAME, PLUS_APP_NAME } from "@environment/env";

type Props = {
  item: TimelineData;
  searchText: string;
  index: number;
  hasEvidence: (timelineReference: string) => boolean;
  readEvidence: (timelineReference: string) => void;
  addEvidence: (item: TimelineData, timelineReference: string) => void;
};

const TimelineItem: React.FC<Props> = (props) => {
  const { item: data, searchText, hasEvidence, readEvidence, addEvidence } = props;

  // ParaPass Quiz Data
  if ("quizSet" in data) {
    const item = data as IParapassQuizData;
    const totalScore = item.score.correct + item.score.incorrect + item.score.unanswered;
    const searchInAppName =
      searchText.trim().length > 0 && PARAPASS_APP_NAME.toLowerCase().includes(searchText.toLowerCase());
    const searchInTitle = searchText.trim().length > 0 && item.title.toLowerCase().includes(searchText.toLowerCase());
    const searchInDate =
      searchText.trim().length > 0 &&
      format(item.sortableDate!, "dd MMM yyyy").toLowerCase().includes(searchText.toLowerCase());

    let splitAppName: string[] = [];
    let splitTitle: string[] = [];
    let splitDate: string[] = [];

    if (searchInAppName) {
      const regex = new RegExp(`(${DataController.escapeRegExp(searchText)})`, "gi");
      splitAppName = PARAPASS_APP_NAME.split(regex);
      splitAppName = splitAppName.filter((item) => item.length > 0);
    }

    if (searchInTitle) {
      const regex = new RegExp(`(${DataController.escapeRegExp(searchText)})`, "gi");
      splitTitle = item.title.split(regex);
      splitTitle = splitTitle.filter((item) => item.length > 0);
    }

    if (searchInDate) {
      const regex = new RegExp(`(${DataController.escapeRegExp(searchText)})`, "gi");
      splitDate = format(item.sortableDate!, "dd MMM yyyy").split(regex);
      splitDate = splitDate.filter((item) => item.length > 0);
    }

    const timelineReference = `${item.quizSet}-${item.date}`;

    return (
      <div
        key={timelineReference}
        className="timelineBlock"
      >
        <div className="timelineBlockInner">
          <div className="timelineBlockHeader">
            <img
              src={isUK ? Icon_ParaPass : Icon_ParaPD}
              alt={isUK ? "Parapass app icon" : "Paramedic PD app icon"}
              className="timelineBlockAppIcon"
            />
            <div className="timelineBlockHeaderAppTitle parapassTitle">
              {splitAppName.length > 0 ? (
                <>
                  {splitAppName.map((split: string, _index: number) => {
                    const searchTerm = split.toLowerCase() === searchText.toLowerCase();

                    if (searchTerm) {
                      return (
                        <span
                          key={`parapass-appTitle-${split}-${_index}`}
                          className="timelineBlockSearchHighlight"
                        >
                          {split}
                        </span>
                      );
                    }

                    return <span key={`parapass-appTitle-${split}-${_index}`}>{split}</span>;
                  })}
                </>
              ) : (
                <>{PARAPASS_APP_NAME}</>
              )}
            </div>
            <div className="timelineBlockDot" />
            <div className="timelineBlockHeaderType">{DataController.getParapassQuizType(item.type)}</div>
          </div>
          <span className="timelineBlockText">
            {"Completed "}
            {splitTitle.length > 0 ? (
              <>
                {splitTitle.map((split: string, _index: number) => {
                  const searchTerm = split.toLowerCase() === searchText.toLowerCase();

                  if (searchTerm) {
                    return (
                      <b
                        key={`parapass-${split}-${_index}`}
                        className="timelineBlockSearchHighlight"
                      >
                        {split}
                      </b>
                    );
                  }

                  return <b key={`parapass-${split}-${_index}`}>{split}</b>;
                })}
              </>
            ) : (
              <b>{item.title}</b>
            )}
            {" quiz and scored "}
            <b>{`${item.score.correct}/${totalScore}`}</b>
          </span>
          <div className="timelineDateText">
            <span>
              {"Taken "}
              {splitDate.length > 0 ? (
                <>
                  {splitDate.map((split: string, _index: number) => {
                    const searchTerm = split.toLowerCase() === searchText.toLowerCase();

                    if (searchTerm) {
                      return (
                        <span
                          key={`parapass-${split}-${_index}`}
                          className="timelineBlockSearchHighlight"
                        >
                          {split}
                        </span>
                      );
                    }

                    return <span key={`parapass-${split}-${_index}`}>{split}</span>;
                  })}
                </>
              ) : (
                <>{`${format(item.sortableDate!, "dd MMM yyyy")}`}</>
              )}
            </span>
          </div>
          {hasEvidence(timelineReference) ? (
            <IonButton
              mode="ios"
              className="timelineReadEvidenceButton"
              onClick={() => readEvidence(timelineReference)}
            >
              <div>{"View"}</div>
            </IonButton>
          ) : (
            <IonButton
              mode="ios"
              className="timelineAddEvidenceButton"
              onClick={() => addEvidence(item, timelineReference)}
            >
              <div className="timelineAddEvidenceButtonContainer">
                <div className="timelineAddEvidenceButtonIconContainer">
                  <IonImg
                    className="timelineAddEvidenceButtonIcon"
                    src={Icon_Add_Evidence}
                  />
                </div>
                <div>{"Add evidence"}</div>
              </div>
            </IonButton>
          )}
        </div>
      </div>
    );
  }

  // ParaPass Usage Data
  if ("ContentType" in data) {
    const item = data as IParapassUsageData;
    const searchInAppName =
      searchText.trim().length > 0 && PARAPASS_APP_NAME.toLowerCase().includes(searchText.toLowerCase());
    const searchInTitle = searchText.trim().length > 0 && item.title.toLowerCase().includes(searchText.toLowerCase());
    const searchInDate =
      searchText.trim().length > 0 &&
      format(item.sortableDate!, "dd MMM yyyy").toLowerCase().includes(searchText.toLowerCase());

    let splitAppName: string[] = [];
    let splitTitle: string[] = [];
    let splitDate: string[] = [];

    if (searchInAppName) {
      const regex = new RegExp(`(${DataController.escapeRegExp(searchText)})`, "gi");
      splitAppName = PARAPASS_APP_NAME.split(regex);
      splitAppName = splitAppName.filter((item) => item.length > 0);
    }

    if (searchInTitle) {
      const regex = new RegExp(`(${DataController.escapeRegExp(searchText)})`, "gi");
      splitTitle = item.title.split(regex);
      splitTitle = splitTitle.filter((item) => item.length > 0);
    }

    if (searchInDate) {
      const regex = new RegExp(`(${DataController.escapeRegExp(searchText)})`, "gi");
      splitDate = format(item.sortableDate!, "dd MMM yyyy").split(regex);
      splitDate = splitDate.filter((item) => item.length > 0);
    }

    const timelineReference = `${item.ContentID}-${new Date(item.TimeStamp).toISOString().split("T")[0]}`;

    return (
      <div
        key={timelineReference}
        className="timelineBlock"
      >
        <div className="timelineBlockInner">
          <div className="timelineBlockHeader">
            <img
              src={isUK ? Icon_ParaPass : Icon_ParaPD}
              alt={isUK ? "Parapass app icon" : "Paramedic PD app icon"}
              className="timelineBlockAppIcon"
            />
            <div className="timelineBlockHeaderAppTitle parapassTitle">
              {splitAppName.length > 0 ? (
                <>
                  {splitAppName.map((split: string, _index: number) => {
                    const searchTerm = split.toLowerCase() === searchText.toLowerCase();
                    if (searchTerm) {
                      return (
                        <span
                          key={`parapass-appTitle-${split}-${_index}`}
                          className="timelineBlockSearchHighlight"
                        >
                          {split}
                        </span>
                      );
                    }
                    return <span key={`parapass-appTitle-${split}-${_index}`}>{split}</span>;
                  })}
                </>
              ) : (
                <>{PARAPASS_APP_NAME}</>
              )}
            </div>
            <div className="timelineBlockDot" />
            <div className="timelineBlockHeaderType">{item.ContentType}</div>
          </div>
          <span className="timelineBlockText">
            {`Viewed ${item.ContentType} `}
            {splitTitle.length > 0 ? (
              <>
                {splitTitle.map((split: string, _index: number) => {
                  const searchTerm = split.toLowerCase() === searchText.toLowerCase();

                  if (searchTerm) {
                    return (
                      <b
                        key={`parapass-${split}-${_index}`}
                        className="timelineBlockSearchHighlight"
                      >
                        {split}
                      </b>
                    );
                  }

                  return <b key={`parapass-${split}-${_index}`}>{split}</b>;
                })}
              </>
            ) : (
              <b>{item.title}</b>
            )}
          </span>
          <div className="timelineDateText">
            <span>
              {`Viewed on `}
              {splitDate.length > 0 ? (
                <>
                  {splitDate.map((split: string, _index: number) => {
                    const searchTerm = split.toLowerCase() === searchText.toLowerCase();

                    if (searchTerm) {
                      return (
                        <span
                          key={`plus-${split}-${_index}`}
                          className="timelineBlockSearchHighlight"
                        >
                          {split}
                        </span>
                      );
                    }

                    return <span key={`plus-${split}-${_index}`}>{split}</span>;
                  })}
                </>
              ) : (
                <>{`${format(item.sortableDate!, "dd MMM yyyy")}`}</>
              )}
            </span>
          </div>
          {hasEvidence(timelineReference) ? (
            <IonButton
              mode="ios"
              className="timelineReadEvidenceButton"
              onClick={() => readEvidence(timelineReference)}
            >
              <div>{"View"}</div>
            </IonButton>
          ) : (
            <IonButton
              mode="ios"
              className="timelineAddEvidenceButton"
              onClick={() => addEvidence(item, timelineReference)}
            >
              <div className="timelineAddEvidenceButtonContainer">
                <div className="timelineAddEvidenceButtonIconContainer">
                  <IonImg
                    className="timelineAddEvidenceButtonIcon"
                    src={Icon_Add_Evidence}
                  />
                </div>
                <div>{"Add evidence"}</div>
              </div>
            </IonButton>
          )}
        </div>
      </div>
    );
  }

  // ParaPass Books Progress
  if (TimelineUtils.isParapassBooksProgress(data)) {
    const item = data as IParapassBooksProgress;
    const searchInAppName =
      searchText.trim().length > 0 && PARAPASS_APP_NAME.toLowerCase().includes(searchText.toLowerCase());
    const searchInTitle = searchText.trim().length > 0 && item.title.toLowerCase().includes(searchText.toLowerCase());
    const searchInChapterName =
      searchText.trim().length > 0 && item.ChapterName?.toLowerCase().includes(searchText.toLowerCase());
    const searchInDate =
      searchText.trim().length > 0 &&
      format(item.sortableDate!, "dd MMM yyyy").toLowerCase().includes(searchText.toLowerCase());

    let splitAppName: string[] = [];
    let splitTitle: string[] = [];
    let splitDate: string[] = [];
    let splitChapterName: string[] = [];

    if (searchInAppName) {
      const regex = new RegExp(`(${DataController.escapeRegExp(searchText)})`, "gi");
      splitAppName = PARAPASS_APP_NAME.split(regex);
      splitAppName = splitAppName.filter((item) => item.length > 0);
    }

    if (searchInTitle) {
      const regex = new RegExp(`(${DataController.escapeRegExp(searchText)})`, "gi");
      splitTitle = item.title.split(regex);
      splitTitle = splitTitle.filter((item) => item.length > 0);
    }

    if (searchInChapterName) {
      const regex = new RegExp(`(${DataController.escapeRegExp(searchText)})`, "gi");
      splitChapterName = item.ChapterName!.split(regex);
      splitChapterName = splitChapterName.filter((item) => item.length > 0);
    }

    if (searchInDate) {
      const regex = new RegExp(`(${DataController.escapeRegExp(searchText)})`, "gi");
      splitDate = format(item.sortableDate!, "dd MMM yyyy").split(regex);
      splitDate = splitDate.filter((item) => item.length > 0);
    }

    const timelineReference = `${item.name}-${new Date(item.time).toISOString().split("T")[0]}`;

    return (
      <div
        key={timelineReference}
        className="timelineBlock"
      >
        <div className="timelineBlockInner">
          <div className="timelineBlockHeader">
            <img
              src={isUK ? Icon_ParaPass : Icon_ParaPD}
              alt={isUK ? "Parapass app icon" : "Paramedic PD app icon"}
              className="timelineBlockAppIcon"
            />
            <div className="timelineBlockHeaderAppTitle parapassTitle">
              {splitAppName.length > 0 ? (
                <>
                  {splitAppName.map((split: string, _index: number) => {
                    const searchTerm = split.toLowerCase() === searchText.toLowerCase();
                    if (searchTerm) {
                      return (
                        <span
                          key={`parapass-appTitle-${split}-${_index}`}
                          className="timelineBlockSearchHighlight"
                        >
                          {split}
                        </span>
                      );
                    }
                    return <span key={`parapass-appTitle-${split}-${_index}`}>{split}</span>;
                  })}
                </>
              ) : (
                <>{PARAPASS_APP_NAME}</>
              )}
            </div>
            <div className="timelineBlockDot" />
            <div className="timelineBlockHeaderType">{TimelineUtils.mapParapassBookFilter(item.filterType)}</div>
          </div>
          <span className="timelineBlockText">
            {"Read "}
            {splitTitle.length > 0 ? (
              <>
                {splitTitle.map((split: string, _index: number) => {
                  const searchTerm = split.toLowerCase() === searchText.toLowerCase();
                  if (searchTerm) {
                    return (
                      <b
                        key={`parapass-${split}-${_index}`}
                        className="timelineBlockSearchHighlight"
                      >
                        {split}
                      </b>
                    );
                  }
                  return <b key={`parapass-${split}-${_index}`}>{split}</b>;
                })}
              </>
            ) : (
              <b>{item.title}</b>
            )}
            {" Book "}
            {item.ChapterName && (
              <>
                {"Chapter "}
                <b>{`${item.ChapterNumber} - `}</b>
                {splitChapterName.length > 0 ? (
                  <>
                    {splitChapterName.map((split: string, _index: number) => {
                      const searchTerm = split.toLowerCase() === searchText.toLowerCase();
                      if (searchTerm) {
                        return (
                          <b
                            key={`parapass-${split}-${_index}`}
                            className="timelineBlockSearchHighlight"
                          >
                            {split}
                          </b>
                        );
                      }
                      return <b key={`parapass-${split}-${_index}`}>{split}</b>;
                    })}
                  </>
                ) : (
                  <b>{item.ChapterName}</b>
                )}
              </>
            )}
          </span>
          <div className="timelineDateText">
            <span>
              {`Read on `}
              {splitDate.length > 0 ? (
                <>
                  {splitDate.map((split: string, _index: number) => {
                    const searchTerm = split.toLowerCase() === searchText.toLowerCase();
                    if (searchTerm) {
                      return (
                        <span
                          key={`parapass-${split}-${_index}`}
                          className="timelineBlockSearchHighlight"
                        >
                          {split}
                        </span>
                      );
                    }
                    return <span key={`parapass-${split}-${_index}`}>{split}</span>;
                  })}
                </>
              ) : (
                <>{`${format(item.sortableDate!, "dd MMM yyyy")}`}</>
              )}
            </span>
          </div>
          {hasEvidence(timelineReference) ? (
            <IonButton
              mode="ios"
              className="timelineReadEvidenceButton"
              onClick={() => readEvidence(timelineReference)}
            >
              <div>{"View"}</div>
            </IonButton>
          ) : (
            <IonButton
              mode="ios"
              className="timelineAddEvidenceButton"
              onClick={() => addEvidence(item, timelineReference)}
            >
              <div className="timelineAddEvidenceButtonContainer">
                <div className="timelineAddEvidenceButtonIconContainer">
                  <IonImg
                    className="timelineAddEvidenceButtonIcon"
                    src={Icon_Add_Evidence}
                  />
                </div>
                <div>{"Add evidence"}</div>
              </div>
            </IonButton>
          )}
        </div>
      </div>
    );
  }

  // JRCALC Plus Usage Data
  if ("GLID" in data) {
    const item = data as IPlusUsageData;
    const searchInAppName =
      searchText.trim().length > 0 && PLUS_APP_NAME.toLowerCase().includes(searchText.toLowerCase());
    const searchInTitle = searchText.trim().length > 0 && item.title.toLowerCase().includes(searchText.toLowerCase());
    const searchInDate =
      searchText.trim().length > 0 &&
      format(item.sortableDate!, "dd MMM yyyy").toLowerCase().includes(searchText.toLowerCase());

    let splitAppName: string[] = [];
    let splitTitle: string[] = [];
    let splitDate: string[] = [];

    if (searchInAppName) {
      const regex = new RegExp(`(${DataController.escapeRegExp(searchText)})`, "gi");
      splitAppName = PLUS_APP_NAME.split(regex);
      splitAppName = splitAppName.filter((item) => item.length > 0);
    }

    if (searchInTitle) {
      const regex = new RegExp(`(${DataController.escapeRegExp(searchText)})`, "gi");
      splitTitle = item.title.split(regex);
      splitTitle = splitTitle.filter((item) => item.length > 0);
    }

    if (searchInDate) {
      const regex = new RegExp(`(${DataController.escapeRegExp(searchText)})`, "gi");
      splitDate = format(item.sortableDate!, "dd MMM yyyy").split(regex);
      splitDate = splitDate.filter((item) => item.length > 0);
    }

    const timelineReference = `${item.GLID}-${item.timestamp.split("T")[0]}`;

    return (
      <div
        key={timelineReference}
        className="timelineBlock"
      >
        <div className="timelineBlockInner">
          <div className="timelineBlockHeader">
            <img
              src={isUK ? Icon_Plus : Icon_CPG}
              alt={isUK ? "JRCALC Plus app icon" : "Paramedic CPG app icon"}
              className="timelineBlockAppIcon"
            />
            <div className="timelineBlockHeaderAppTitle plusTitle">
              {splitAppName.length > 0 ? (
                <>
                  {splitAppName.map((split: string, _index: number) => {
                    const searchTerm = split.toLowerCase() === searchText.toLowerCase();

                    if (searchTerm) {
                      return (
                        <span
                          key={`plus-appTitle-${split}-${_index}`}
                          className="timelineBlockSearchHighlight"
                        >
                          {split}
                        </span>
                      );
                    }

                    return <span key={`plus-appTitle-${split}-${_index}`}>{split}</span>;
                  })}
                </>
              ) : (
                <>{PLUS_APP_NAME}</>
              )}
            </div>
          </div>
          <span className="timelineBlockText">
            {`Viewed ${DataController.getJRCALCGuidelineType(item.GLID)} `}
            {splitTitle.length > 0 ? (
              <>
                {splitTitle.map((split: string, _index: number) => {
                  const searchTerm = split.toLowerCase() === searchText.toLowerCase();

                  if (searchTerm) {
                    return (
                      <b
                        key={`plus-${split}-${_index}`}
                        className="timelineBlockSearchHighlight"
                      >
                        {split}
                      </b>
                    );
                  }

                  return <b key={`plus-${split}-${_index}`}>{split}</b>;
                })}
              </>
            ) : (
              <b>{item.title}</b>
            )}
          </span>
          <div className="timelineDateText">
            <span>
              {`Viewed ${item.count ? `${item.count} time${item.count === 1 ? "" : "s"} on ` : ""}`}
              {splitDate.length > 0 ? (
                <>
                  {splitDate.map((split: string, _index: number) => {
                    const searchTerm = split.toLowerCase() === searchText.toLowerCase();

                    if (searchTerm) {
                      return (
                        <span
                          key={`plus-${split}-${_index}`}
                          className="timelineBlockSearchHighlight"
                        >
                          {split}
                        </span>
                      );
                    }

                    return <span key={`plus-${split}-${_index}`}>{split}</span>;
                  })}
                </>
              ) : (
                <>{`${format(item.sortableDate!, "dd MMM yyyy")}`}</>
              )}
            </span>
          </div>
          {hasEvidence(timelineReference) ? (
            <IonButton
              mode="ios"
              className="timelineReadEvidenceButton"
              onClick={() => readEvidence(timelineReference)}
            >
              <div>{"View"}</div>
            </IonButton>
          ) : (
            <IonButton
              mode="ios"
              className="timelineAddEvidenceButton"
              onClick={() => addEvidence(item, timelineReference)}
            >
              <div className="timelineAddEvidenceButtonContainer">
                <div className="timelineAddEvidenceButtonIconContainer">
                  <IonImg
                    className="timelineAddEvidenceButtonIcon"
                    src={Icon_Add_Evidence}
                  />
                </div>
                <div>{"Add evidence"}</div>
              </div>
            </IonButton>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default TimelineItem;
